'use client';
import { Button } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import BDImage from '@shared/components/elements/bd-image';
import BDSwitch from '@shared/components/elements/switch';
import { MetalTypes, getProductFilterLabel, ringOptions } from '@shared/lib/search/product';
import { Image as ImageType, ProductVariant } from '@shared/lib/shopify/types';
import { Cart } from '@shared/lib/shopify/types/shopify-native';
import {
  getAllJewelryFiltersAsArray,
  getCartFilters,
  getCutFilters,
  getRingFilters,
} from '@shared/lib/utils/jewelry-filter-utils';
import {
  clearAllFilters,
  setFilterUrlFilterChoice,
  toggleUseCartFilter,
} from '@shared/lib/utils/url-utils';
import clsx from 'clsx';
import { StaticImageData } from 'next/image';
import { ReadonlyURLSearchParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Metal } from '../metal';

interface SelectedFiltersProps {
  result_count: number;
  cart: Cart | undefined;
}

export const SelectedFilters: React.FC<SelectedFiltersProps> = ({ result_count, cart }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const allSelectedFilters = getAllJewelryFiltersAsArray(cart, searchParams);
  const { canUseCartFilters, useCartFilters } = getCartFilters(cart, searchParams);
  const shouldClearCartFilters = useCartFilters && canUseCartFilters;
  return (
    <div className="@container flex flex-1 items-end">
      {/* <div className="flex flex-1 flex-col gap-2 @xs:flex-row @xs:flex-wrap @xs:items-start @xs:justify-between"> */}
      <ul
        className={clsx(
          'flex flex-row flex-wrap gap-x-2 gap-y-1'
          // allSelectedFilters.length > 1 && 'w-full'
        )}
      >
        {allSelectedFilters.map(({ key, value }) => (
          <li key={value}>
            <button
              key={value}
              className="bg-bd-contrast hover:bg-bd-contrast/80 shadow-xs group flex cursor-pointer items-center rounded-sm px-2 py-1"
              onClick={() => {
                let newSearchParams: URLSearchParams | ReadonlyURLSearchParams = searchParams;
                if (shouldClearCartFilters) {
                  newSearchParams = toggleUseCartFilter(true, router, pathname, searchParams);
                }
                setFilterUrlFilterChoice(
                  router,
                  pathname,
                  newSearchParams,
                  key,
                  value.toString(),
                  false,
                  false
                );
              }}
            >
              <span className="text-sm capitalize">{getProductFilterLabel(key, value)}</span>
              <XMarkIcon className="ml-1 h-4 w-4 cursor-pointer" />
            </button>
          </li>
        ))}
        {allSelectedFilters.length > 1 && (
          <li key="clear">
            <button
              className="bg-bd-contrast hover:bg-bd-contrast/80 shadow-xs group flex cursor-pointer items-center rounded-sm px-2 py-1"
              onClick={() => clearAllFilters(router, pathname, searchParams, false)}
            >
              <span className="text-nowrap text-sm">Clear All</span>
              <XMarkIcon className="ml-1 h-4 w-4 cursor-pointer" />
            </button>
          </li>
        )}
      </ul>
      {/* </div> */}
      <CartFilterToggle
        cart={cart}
        className="ml-auto hidden flex-row flex-wrap items-end justify-end gap-2 md:flex"
      />
    </div>
  );
};

export const CartFilterToggle: React.FC<{ cart: Cart | undefined; className?: string }> = ({
  cart,
  className,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { canUseCartFilters, useCartFilters } = getCartFilters(cart, searchParams);
  if (!canUseCartFilters) return null;
  return (
    <div className={clsx('flex', className)}>
      <span className="text-nowrap text-sm">Fit selected diamonds</span>
      <BDSwitch
        checked={useCartFilters}
        disabled={!canUseCartFilters}
        onChange={() => toggleUseCartFilter(useCartFilters, router, pathname, searchParams)}
        className="ml-auto shrink-0"
      ></BDSwitch>
    </div>
  );
};
interface SideFiltersProps {
  result_count: number;
  shapeOptions: Record<string, ProductVariant>;
  cart: Cart | undefined;
  productType: string | undefined;
  onClose?: () => void;
}
const SideFilters: React.FC<SideFiltersProps> = ({
  result_count,
  shapeOptions,
  cart,
  productType,
  onClose,
}) => {
  const searchParams = useSearchParams();
  const { canUseCartFilters, useCartFilters } = getCartFilters(cart, searchParams);
  const cutChangeShouldClearCartFilters = useCartFilters && canUseCartFilters;
  return (
    <div className="text-bd-dark/80 flex h-full flex-col gap-4 pb-2">
      <div className="flex shrink-0 flex-col space-y-2 pt-4">
        <h3 className="text-bd-primary text-lg font-light uppercase">Filters</h3>
        <SelectedFilters result_count={result_count} cart={cart} />
      </div>

      <div className="flex grow overflow-y-auto overflow-x-hidden pb-12">
        <nav className="flex flex-col gap-y-4 pb-12">
          {/* Diamond Cut */}
          <FilterDropdown
            label="Fit Diamond Shape"
            options={Object.entries(shapeOptions).reduce(
              (acc, [shape, variant]) => {
                acc[shape] = { label: shape, img: variant.image };
                return acc;
              },
              {} as Record<string, { label: string; img: ImageType }>
            )}
            selectedOptions={getCutFilters(cart, searchParams)}
            urlParam="cut"
            shouldClearCartFilters={cutChangeShouldClearCartFilters}
            clearSibling={true}
            onClose={onClose}
          />
          <ColorFilter label="Metal" options={MetalTypes} urlParam="metal" onClose={onClose} />
          {/* Ring Style */}
          {productType === 'engagement_rings' && (
            <FilterDropdown
              label="Ring Style"
              options={ringOptions}
              urlParam="ring"
              selectedOptions={getRingFilters(searchParams)}
              shouldClearCartFilters={false}
              clearSibling={false}
            />
          )}
        </nav>
      </div>
    </div>
  );
};

interface FilterDropdownProps {
  label: string;
  options: Record<string, { label: string; img: StaticImageData | ImageType }>;
  selectedOptions: string[];
  urlParam: string;
  shouldClearCartFilters: boolean;
  clearSibling: boolean;
  onClose?: () => void;
}
const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  selectedOptions,
  urlParam,
  shouldClearCartFilters = false,
  clearSibling,
  onClose,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  return (
    <div key={label} className="relative">
      <div className="border-t-bd-contrast bg-bd-light text-bd-primary sticky top-0 z-10 flex border-b-0 border-t px-4 py-1.5 text-base leading-6">
        <h3>{label}</h3>
      </div>
      <ul role="list" className="grid grid-cols-4 gap-2 pt-2">
        {Object.entries(options).map(([key, { label, img }]) => (
          <li key={key} className="flex flex-col items-center gap-2 px-2">
            <Button
              className="group min-h-20 min-w-20 cursor-pointer"
              onClick={() => {
                let newSearchParams: URLSearchParams | ReadonlyURLSearchParams = searchParams;
                if (shouldClearCartFilters) {
                  newSearchParams = toggleUseCartFilter(true, router, pathname, searchParams);
                }
                setFilterUrlFilterChoice(
                  router,
                  pathname,
                  newSearchParams,
                  urlParam,
                  key,
                  !selectedOptions.includes(key),
                  clearSibling
                );
                onClose?.();
              }}
            >
              <BDImage
                src={img}
                alt={`${label} image`}
                className={clsx(
                  'group-hover:border-bd-primary border-bd-contrast h-20 w-20 rounded-full border-2',
                  selectedOptions.includes(key) ? 'border-bd-primary' : ''
                )}
              />
              <div className="mt-1 min-w-0">
                <p className="text-bd-dark text-sm capitalize leading-6">{label}</p>
              </div>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

interface ColorFilterProps {
  label: string;
  options: string[];
  urlParam: string;
  onClose?: () => void;
}

const ColorFilter: React.FC<ColorFilterProps> = ({ label, options, urlParam, onClose }) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const selectedOptions = searchParams.getAll(urlParam);
  return (
    <div key={label} className="relative">
      <div className="border-t-bd-contrast bg-bd-light text-bd-primary sticky top-0 z-10 flex border-b-0 border-t px-4 py-1.5 text-base leading-6">
        <h3>{label}</h3>
      </div>
      <ul role="list" className="mt-2 grid grid-cols-4 gap-2">
        {options.map((option) => (
          <li key={option} className="flex flex-col items-center gap-2 px-2">
            <Button
              className="group cursor-pointer"
              onClick={() => {
                setFilterUrlFilterChoice(
                  router,
                  pathname,
                  searchParams,
                  urlParam,
                  option,
                  !selectedOptions.includes(option),
                  true
                );
                onClose?.();
              }}
            >
              <div className="flex min-w-0 flex-col items-center gap-2">
                <Metal
                  metal={option}
                  className={clsx(
                    selectedOptions.includes(option)
                      ? 'border-bd-primary border-2'
                      : 'border-transparent',
                    'group-hover:border-bd-primary h-10 w-10 group-hover:border-2'
                  )}
                />
                <p className="text-bd-dark text-sm capitalize leading-6">
                  {getProductFilterLabel('metal', option)}
                </p>
              </div>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SideFilters;
